'use strict';

class BannerSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.slider = this.querySelector('.swiper');
    this.nextBtn = this.querySelector('.slider-button--next');
    this.prevBtn = this.querySelector('.slider-button--prev');
    this.pagination = this.querySelector('.slideshow__control-wrapper');
    this.paginationButtons = this.querySelectorAll('.slider-counter__link');
    
    // Overlay elements
    this.overlay1 = this.querySelector('.banner-slider__overlay--1');
    this.overlay2 = this.querySelector('.banner-slider__overlay--2');
    this.decorativeIcon = this.querySelector('.banner-slider__decorative-icon');

    this.handleResize = this.handleResize.bind(this);
    this.updateNavigationButtons = this.updateNavigationButtons.bind(this);
    this.updateOverlayElements = this.updateOverlayElements.bind(this);
  }
  
  connectedCallback() {
    this.initSwiper();
    this.setupExternalNavigation();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  updateNavigationButtons() {
    if (!this.swiper || this.swiper.params.loop) return;

    // Update pagination
    this.paginationButtons.forEach((btn, index) => {
      btn.classList.toggle('active', index === this.swiper.activeIndex);
    });

    // Update desktop navigation buttons
    if (this.prevBtn) {
      this.prevBtn.classList.toggle('slider-button-disabled', this.swiper.isBeginning);
      this.prevBtn.toggleAttribute('disabled', this.swiper.isBeginning);
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle('slider-button-disabled', this.swiper.isEnd);
      this.nextBtn.toggleAttribute('disabled', this.swiper.isEnd);
    }

    // Update mobile navigation buttons
    const mobileNextBtn = this.querySelector('.slider-buttons .slider-button--next');
    const mobilePrevBtn = this.querySelector('.slider-buttons .slider-button--prev');
    
    if (mobilePrevBtn) {
      mobilePrevBtn.classList.toggle('slider-button-disabled', this.swiper.isBeginning);
      mobilePrevBtn.toggleAttribute('disabled', this.swiper.isBeginning);
    }
    if (mobileNextBtn) {
      mobileNextBtn.classList.toggle('slider-button-disabled', this.swiper.isEnd);
      mobileNextBtn.toggleAttribute('disabled', this.swiper.isEnd);
    }
  }

  updateOverlayElements() {
    if (!this.swiper) {
      return;
    }

    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (!activeSlide) {
      return;
    }

    // Update overlay 1
    if (this.overlay1) {
      const overlay1Src = activeSlide.getAttribute('data-overlay-1');
      if (overlay1Src && overlay1Src.trim() !== '') {
        // Ensure URL has proper protocol
        const fullUrl = overlay1Src.startsWith('//') ? 'https:' + overlay1Src : overlay1Src;

        // Update image source only if different
        const currentImg = this.overlay1.querySelector('img');
        if (!currentImg || currentImg.src !== fullUrl) {
          this.overlay1.innerHTML = `<img src="${fullUrl}" loading="lazy" alt="Overlay 1" />`;
        }

        // Set CSS custom properties for responsive positioning
        this.overlay1.style.setProperty('--overlay-1-left', activeSlide.getAttribute('data-overlay-1-left') + '%');
        this.overlay1.style.setProperty('--overlay-1-top', activeSlide.getAttribute('data-overlay-1-top') + '%');
        this.overlay1.style.setProperty('--overlay-1-width', activeSlide.getAttribute('data-overlay-1-width') + '%');
        this.overlay1.style.setProperty('--overlay-1-left-mobile', activeSlide.getAttribute('data-overlay-1-left-mobile') + '%');
        this.overlay1.style.setProperty('--overlay-1-top-mobile', activeSlide.getAttribute('data-overlay-1-top-mobile') + '%');

        // Set desktop positioning
        this.overlay1.style.left = activeSlide.getAttribute('data-overlay-1-left') + '%';
        this.overlay1.style.top = activeSlide.getAttribute('data-overlay-1-top') + '%';
        this.overlay1.style.width = activeSlide.getAttribute('data-overlay-1-width') + '%';
        this.overlay1.style.display = 'block';
      } else {
        this.overlay1.style.display = 'none';
      }
    }

    // Update overlay 2
    if (this.overlay2) {
      const overlay2Src = activeSlide.getAttribute('data-overlay-2');
      if (overlay2Src && overlay2Src.trim() !== '') {
        // Ensure URL has proper protocol
        const fullUrl = overlay2Src.startsWith('//') ? 'https:' + overlay2Src : overlay2Src;
        
        // Update image source only if different
        const currentImg = this.overlay2.querySelector('img');
        if (!currentImg || currentImg.src !== fullUrl) {
          this.overlay2.innerHTML = `<img src="${fullUrl}" loading="lazy" alt="Overlay 2" />`;
        }
        
        // Set CSS custom properties for responsive positioning
        this.overlay2.style.setProperty('--overlay-2-left', activeSlide.getAttribute('data-overlay-2-left') + '%');
        this.overlay2.style.setProperty('--overlay-2-top', activeSlide.getAttribute('data-overlay-2-top') + '%');
        this.overlay2.style.setProperty('--overlay-2-width', activeSlide.getAttribute('data-overlay-2-width') + '%');
        this.overlay2.style.setProperty('--overlay-2-left-mobile', activeSlide.getAttribute('data-overlay-2-left-mobile') + '%');
        this.overlay2.style.setProperty('--overlay-2-top-mobile', activeSlide.getAttribute('data-overlay-2-top-mobile') + '%');
        
        // Set desktop positioning
        this.overlay2.style.left = activeSlide.getAttribute('data-overlay-2-left') + '%';
        this.overlay2.style.top = activeSlide.getAttribute('data-overlay-2-top') + '%';
        this.overlay2.style.width = activeSlide.getAttribute('data-overlay-2-width') + '%';
        this.overlay2.style.display = 'block';
      } else {
        this.overlay2.style.display = 'none';
      }
    }

    // Update decorative icon
    if (this.decorativeIcon) {
      const iconSrc = activeSlide.getAttribute('data-icon');
      if (iconSrc && iconSrc.trim() !== '') {
        // Ensure URL has proper protocol
        const fullUrl = iconSrc.startsWith('//') ? 'https:' + iconSrc : iconSrc;
        
        // Update image source only if different
        const currentImg = this.decorativeIcon.querySelector('img');
        if (!currentImg || currentImg.src !== fullUrl) {
          this.decorativeIcon.innerHTML = `<img src="${fullUrl}" loading="lazy" alt="Decorative icon" />`;
        }
        
        // Set CSS custom properties for responsive positioning
        this.decorativeIcon.style.setProperty('--icon-left', activeSlide.getAttribute('data-icon-left') + '%');
        this.decorativeIcon.style.setProperty('--icon-top', activeSlide.getAttribute('data-icon-top') + '%');
        this.decorativeIcon.style.setProperty('--icon-left-mobile', activeSlide.getAttribute('data-icon-left-mobile') + '%');
        this.decorativeIcon.style.setProperty('--icon-top-mobile', activeSlide.getAttribute('data-icon-top-mobile') + '%');
        
        // Set desktop positioning
        this.decorativeIcon.style.left = activeSlide.getAttribute('data-icon-left') + '%';
        this.decorativeIcon.style.top = activeSlide.getAttribute('data-icon-top') + '%';
        this.decorativeIcon.style.width = activeSlide.getAttribute('data-icon-width') + '%';
        this.decorativeIcon.style.display = 'block';
      } else {
        this.decorativeIcon.style.display = 'none';
      }
    }
  }

  initSwiper() {
    if (!this.slider) {
      return;
    }

    const {
      speed = "500",
      loop = "false",
      centeredSlides = "false",
      slidesCount = "1",
      slidesCountMobile = "1",
      spaceBetween = "0",
      direction = "horizontal",
      directionMobile = "horizontal",
      initialSlide = "0"
    } = this.dataset;

    const slidesCountValue = slidesCount === "auto" ? "auto" : Number(slidesCount);
    const slidesCountMobileValue = slidesCountMobile === "auto" ? "auto" : Number(slidesCountMobile);
    const spaceBetweenValue = Number(spaceBetween);
    const speedValue = Number(speed);
    const isLoop = loop === "true";
    const isCentered = centeredSlides === "true";
    const isDirection = direction === "horizontal" ? "horizontal" : "vertical";
    const isDirectionMobile = directionMobile === "horizontal" ? "horizontal" : "vertical";
    const initialSlideValue = initialSlide ? Number(initialSlide) : 0;

    const swiperOptions = {
      loop: isLoop,
      slidesPerView: slidesCountMobileValue,
      loopAdditionalSlides: 2,
      centeredSlides: isCentered,
      initialSlide: initialSlideValue,
      spaceBetween: spaceBetweenValue, 
      grabCursor: true,
      speed: speedValue,
      direction: isDirectionMobile,
      keyboard: {
        enabled: true,
      },
      breakpoints: {
        750: {
          spaceBetween: spaceBetweenValue, 
          slidesPerView: slidesCountValue,
          centeredSlides: isCentered,
          direction: isDirection,
        },
      },
      pagination: this.pagination ? {
        el: this.pagination,
        clickable: true,
        renderBullet: (index, className) => {
          const isActive = index === this.swiper?.activeIndex;
          return `<button class="slider-counter__link slider-counter__link--dots link ${className} ${isActive ? 'active' : ''}" tabindex="-1" aria-label="Load slide ${index + 1}">
            <span class="dot"></span>
          </button>`;
        },
      } : false,
      on: {
        init: () => {
          this.updateNavigationButtons();
          this.updateOverlayElements();
        },
        slideChange: () => {
          this.updateNavigationButtons();
          this.updateOverlayElements();
        },
      }
    };

    setTimeout(() => {
      this.swiper = new Swiper(this.slider, swiperOptions);
      this.updateNavigationButtons();
      this.updateOverlayElements();
    }, 100);
  }

  setupExternalNavigation() {
    // Desktop navigation buttons
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.swiper) {
          this.swiper.slidePrev();
        }
      });
    }

    // Mobile navigation buttons
    const mobileNextBtn = this.querySelector('.slider-buttons .slider-button--next');
    const mobilePrevBtn = this.querySelector('.slider-buttons .slider-button--prev');
    
    if (mobileNextBtn) {
      mobileNextBtn.addEventListener('click', () => {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      });
    }

    if (mobilePrevBtn) {
      mobilePrevBtn.addEventListener('click', () => {
        if (this.swiper) {
          this.swiper.slidePrev();
        }
      });
    }

    // Pagination buttons
    this.paginationButtons.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        if (this.swiper) {
          this.swiper.slideTo(index);
        }
      });
    });
  }
}

if (!customElements.get('banner-slider')) {
  customElements.define('banner-slider', BannerSlider);
}
